import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/categories';
import { matchesByCategory } from '@/mocks/matches';
import { getArticlesByCategory } from '@/mocks/articles';
import type { Category } from '@/mocks/categories';
import type { Match } from '@/mocks/matches';
import type { Article } from '@/mocks/articles';

type TabKey = 'matches' | 'articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState<TabKey>('matches');

  const currentCategory: Category | undefined = useMemo(
    () => categories.find((c) => c.slug === slug),
    [slug],
  );

  const categoryMatches: Match[] = useMemo(
    () => (slug ? matchesByCategory[slug] || [] : []),
    [slug],
  );

  const categoryArticles: Article[] = useMemo(
    () => (slug ? getArticlesByCategory(slug) : []),
    [slug],
  );

  if (!currentCategory) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-20 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-800">分类未找到</h1>
          <Link to="/" className="mt-4 inline-block text-primary-600 hover:underline cursor-pointer">返回首页</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name}赛事 - 开云体育平台`,
    description: currentCategory.description,
    url: `https://www.kaiyunsports.com/category/${slug}`,
    isPartOf: { '@type': 'WebSite', name: '开云体育平台', url: 'https://www.kaiyunsports.com' },
  };

  const articleImageMap: Record<string, string> = {
    'article-wcq-2026': 'https://readdy.ai/api/search-image?query=Professional%20soccer%20match%20scene%20with%20players%20in%20action%20on%20green%20field%2C%20dramatic%20stadium%20lighting%2C%20warm%20golden%20tones%2C%20editorial%20sports%20photography%20style%2C%20shallow%20depth%20of%20field%2C%20cinematic%20motion%20blur%2C%20clean%20background&width=800&height=400&seq=article-wcq-2026&orientation=landscape',
    'article-nba-preview': 'https://readdy.ai/api/search-image?query=Dynamic%20basketball%20game%20action%20with%20player%20dunking%2C%20dramatic%20arena%20lighting%2C%20warm%20orange%20and%20red%20tones%2C%20editorial%20sports%20photography%2C%20motion%20capture%2C%20clean%20background%2C%20professional%20composition&width=800&height=400&seq=article-nba-preview&orientation=landscape',
    'article-wimbledon-2026': 'https://readdy.ai/api/search-image?query=Elegant%20tennis%20match%20on%20grass%20court%20with%20player%20serving%2C%20warm%20sunlight%2C%20classic%20Wimbledon%20aesthetic%2C%20editorial%20sports%20photography%2C%20shallow%20depth%20of%20field%2C%20clean%20green%20background&width=800&height=400&seq=article-wimbledon-2026&orientation=landscape',
    'article-lol-s16': 'https://readdy.ai/api/search-image?query=Epic%20esports%20arena%20with%20dramatic%20stage%20lighting%20and%20large%20screens%2C%20competitive%20gaming%20atmosphere%2C%20neon%20red%20and%20gold%20accents%2C%20cinematic%20wide%20shot%2C%20crowded%20venue%2C%20professional%20photography&width=800&height=400&seq=article-lol-s16&orientation=landscape',
    'article-ucl-draw': 'https://readdy.ai/api/search-image?query=European%20champions%20league%20trophy%20and%20stadium%20atmosphere%2C%20dramatic%20night%20lighting%2C%20golden%20and%20deep%20red%20tones%2C%20editorial%20sports%20photography%2C%20cinematic%20composition%2C%20star%20field%20lights&width=800&height=400&seq=article-ucl-draw&orientation=landscape',
    'article-register-guide': 'https://readdy.ai/api/search-image?query=Modern%20digital%20sports%20platform%20interface%20with%20warm%20lighting%2C%20sleek%20design%20aesthetic%2C%20red%20and%20gold%20accents%2C%20clean%20professional%20look%2C%20abstract%20sports%20background%2C%20soft%20gradients&width=800&height=400&seq=article-register-guide&orientation=landscape',
    'article-csl-midseason': 'https://readdy.ai/api/search-image?query=Chinese%20Super%20League%20soccer%20match%20with%20players%20competing%2C%20packed%20stadium%2C%20dramatic%20daylight%2C%20warm%20golden%20hour%20lighting%2C%20editorial%20sports%20photography%2C%20vibrant%20atmosphere&width=800&height=400&seq=article-csl-midseason&orientation=landscape',
    'article-fiba-china': 'https://readdy.ai/api/search-image?query=Chinese%20national%20basketball%20team%20in%20action%20during%20international%20game%2C%20dramatic%20arena%20lighting%2C%20red%20and%20gold%20team%20colors%2C%20editorial%20sports%20photography%2C%20motion%20capture%2C%20professional%20composition&width=800&height=400&seq=article-fiba-china&orientation=landscape',
  };

  const statusLabels: Record<string, string> = {
    live: '进行中',
    upcoming: '即将开始',
    finished: '已结束',
  };

  const statusColors: Record<string, string> = {
    live: 'bg-primary-500',
    upcoming: 'bg-accent-500',
    finished: 'bg-foreground-400',
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categoryPageSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-20">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '赛事分类', href: '/category/football' },
                  { label: currentCategory.name },
                ]}
              />
            </div>

            <div className="mb-8">
              <div className="flex items-center gap-4 mb-3">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className={`${currentCategory.icon} text-xl`}></i>
                </div>
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                    {currentCategory.name}赛事
                  </h1>
                  <p className="text-sm text-foreground-500 mt-1">{currentCategory.description}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-6 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit mb-8">
              <button
                onClick={() => setActiveTab('matches')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === 'matches'
                    ? 'bg-background-50 text-foreground-900'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <i className="ri-trophy-line mr-1.5"></i>
                赛事列表
              </button>
              <button
                onClick={() => setActiveTab('articles')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === 'articles'
                    ? 'bg-background-50 text-foreground-900'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <i className="ri-article-line mr-1.5"></i>
                相关资讯
              </button>
            </div>

            {activeTab === 'matches' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {categoryMatches.length > 0 ? (
                  categoryMatches.map((match) => (
                    <div
                      key={match.id}
                      className="bg-background-100 rounded-xl p-5 border border-background-200/60 hover:border-primary-200/60 transition-all duration-300"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-xs text-foreground-500 font-medium">{match.league}</span>
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium text-background-50 ${statusColors[match.status]}`}
                        >
                          {match.status === 'live' && (
                            <span className="w-1.5 h-1.5 rounded-full bg-background-50 animate-pulse"></span>
                          )}
                          {statusLabels[match.status]}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex-1 text-center">
                          <p className="font-heading text-base font-semibold text-foreground-800">{match.homeTeam}</p>
                        </div>
                        <div className="px-4">
                          {match.status === 'upcoming' ? (
                            <span className="text-lg font-bold text-foreground-400 font-heading">VS</span>
                          ) : (
                            <span className="text-xl font-bold text-primary-600 font-heading">
                              {match.homeScore} - {match.awayScore}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 text-center">
                          <p className="font-heading text-base font-semibold text-foreground-800">{match.awayTeam}</p>
                        </div>
                      </div>
                      <div className="pt-3 border-t border-background-200/60">
                        <span className="text-xs text-foreground-500">{match.time}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-full text-center py-16">
                    <i className="ri-inbox-line text-4xl text-foreground-300"></i>
                    <p className="mt-3 text-foreground-500">暂无赛事数据</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {categoryArticles.length > 0 ? (
                  categoryArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="group bg-background-100 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200/60 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative h-44 overflow-hidden">
                        <img
                          src={articleImageMap[article.imageSeq] || articleImageMap['article-wcq-2026']}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          title={article.title}
                        />
                      </div>
                      <div className="p-5">
                        <h3 className="font-heading text-base font-semibold text-foreground-800 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {article.title}
                        </h3>
                        <p className="mt-2 text-sm text-foreground-500 line-clamp-2">{article.summary}</p>
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-background-200/60">
                          <span className="text-xs text-foreground-400">{article.date}</span>
                          <span className="text-xs text-foreground-400">
                            <i className="ri-eye-line mr-1"></i>
                            {article.readCount.toLocaleString()} 阅读
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))
                ) : (
                  <div className="col-span-full text-center py-16">
                    <i className="ri-inbox-line text-4xl text-foreground-300"></i>
                    <p className="mt-3 text-foreground-500">暂无相关资讯</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}